/**
 * Transfer wizard progress payload, save, and parse-from-json.
 */
import { useCallback, type MutableRefObject } from 'react'
import { showApiError } from '@/lib/utils/notifications'
import * as transferApi from '../api/transferApi'
import { buildTransferProgressPayload } from '../utils/transferProcessProgressPayload'
import type { TransferProgressFlagOverrides } from '../utils/transferProcessProgressPayload'
import { parseTransferProgressData } from '../utils/transferParseProgressData'
import { resolveTransferProcessLogStatus } from '@/features/company/utils/libertyInternalNotesDisplay'
import type { PropertyStaffRow } from '../types'
import type { TransferProcessWorkflowDeps } from './transferWorkflowTypes'

export function useTransferProcessProgress(
  { routing, dialogs, form, core }: TransferProcessWorkflowDeps,
  firsttimeRef?: MutableRefObject<boolean>,
) {
  const { fragment, entityId, statusParam, sourceParam } = routing
  const { process_id, process_id_notes, setProcess_id_notes } = dialogs
  const { reset } = form
  const {
    progressData,
    setProgressData,
    selectedTab,
    selectedSubTab,
    infoDone,
    domains,
    newdomains,
    newPropertySave,
    companyList,
    companyListOld,
    propertyList,
    removestaffList,
    transferstaffList,
    addedstaffList,
    commitstaffList,
    commitDone,
    verifyDone,
    empDone,
    primaryContactSet,
    residentDone,
    residentcount,
    applicantcount,
    setPropertyList,
    setCompanyList,
    setLoadNewCompany,
    setCompanyListOld,
    setInfoDone,
    setNewPropertySave,
    setDomains,
    setNewdomains,
    setRemovestaffList,
    setTransferstaffList,
    setAddedstaffList,
    setCommitstaffList,
    setCommitDone,
    setVerifyDone,
    setEmpDone,
    setPrimaryContactSet,
    setSelectedTab,
    setSelectedSubTab,
    setResidentDone,
    setResidentcount,
    setApplicantcount,
  } = core

  const { getValues } = form

  const getTransferProgressPayload = useCallback(
    (overrideCommitList?: PropertyStaffRow[], flagOverrides?: TransferProgressFlagOverrides) =>
      buildTransferProgressPayload({
        progressData,
        selectedTab,
        infoDone,
        getValues,
        domains,
        newdomains,
        newPropertySave,
        companyList,
        companyListOld,
        propertyList,
        removestaffList,
        transferstaffList,
        addedstaffList,
        commitstaffList,
        commitDone,
        verifyDone,
        empDone,
        primaryContactSet,
        residentDone,
        residentcount,
        applicantcount,
        overrideCommitList,
        flagOverrides,
      }),
    [
      progressData,
      selectedTab,
      infoDone,
      getValues,
      domains,
      newdomains,
      newPropertySave,
      companyList,
      companyListOld,
      propertyList,
      removestaffList,
      transferstaffList,
      addedstaffList,
      commitstaffList,
      commitDone,
      verifyDone,
      empDone,
      primaryContactSet,
      residentDone,
      residentcount,
      applicantcount,
    ],
  )

  const saveTransferProgressInternal = useCallback(
    async (
      commitListOverride?: PropertyStaffRow[],
      flagOverrides?: TransferProgressFlagOverrides,
    ) => {
      if (statusParam) return
      const pd = getTransferProgressPayload(commitListOverride, flagOverrides)
      const logStatus = resolveTransferProcessLogStatus(fragment, selectedTab, selectedSubTab)
      const requestData: Record<string, unknown> = {
        process_name: fragment,
        status: logStatus,
        json_content: pd,
      }
      requestData[fragment === 'transfer_company' ? 'fk_company_id' : 'fk_property_id'] =
        entityId
      if (process_id) requestData.process_id = process_id
      if (sourceParam === 'aln') requestData.source = 'aln'
      try {
        await transferApi.postTransferProcessLog(requestData)
        if (!process_id_notes) {
          const rows = await transferApi.getTransferProcessLog({
            process_name: fragment || '',
            status: logStatus,
            ...(fragment === 'transfer_company'
              ? { company_id: entityId }
              : { property_id: entityId }),
          })
          if (rows[0]?.id) setProcess_id_notes(String(rows[0].id))
        }
      } catch (e) {
        showApiError(e)
      }
    },
    [
      statusParam,
      getTransferProgressPayload,
      fragment,
      selectedTab,
      selectedSubTab,
      entityId,
      process_id,
      sourceParam,
      process_id_notes,
      setProcess_id_notes,
    ],
  )

  const parseProgressData = useCallback(
    (pd: Parameters<typeof parseTransferProgressData>[0]) => {
      parseTransferProgressData(pd, {
        setPropertyList,
        setCompanyList,
        setLoadNewCompany,
        setCompanyListOld,
        setInfoDone,
        setNewPropertySave,
        setDomains,
        setNewdomains,
        resetForm: (values) => reset(values),
        onFirstFormReset: () => {
          if (firsttimeRef) firsttimeRef.current = false
        },
        setRemovestaffList,
        setTransferstaffList,
        setAddedstaffList,
        setCommitstaffList,
        setCommitDone,
        setVerifyDone,
        setEmpDone,
        setPrimaryContactSet,
        setSelectedTab,
        setSelectedSubTab,
        setResidentDone,
        setResidentcount,
        setApplicantcount,
      })
    },
    [
      reset,
      setPropertyList,
      setCompanyList,
      setLoadNewCompany,
      setCompanyListOld,
      setInfoDone,
      setNewPropertySave,
      setDomains,
      setNewdomains,
      setRemovestaffList,
      setTransferstaffList,
      setAddedstaffList,
      setCommitstaffList,
      setCommitDone,
      setVerifyDone,
      setEmpDone,
      setPrimaryContactSet,
      setSelectedTab,
      setSelectedSubTab,
      setResidentDone,
      setResidentcount,
      setApplicantcount,
      firsttimeRef,
    ],
  )

  const saveTransferProgress = useCallback(
    async (flagOverrides?: TransferProgressFlagOverrides) => {
      setProgressData(getTransferProgressPayload(undefined, flagOverrides))
      await saveTransferProgressInternal(undefined, flagOverrides)
    },
    [getTransferProgressPayload, saveTransferProgressInternal, setProgressData],
  )

  return {
    getTransferProgressPayload,
    saveTransferProgressInternal,
    saveTransferProgress,
    parseProgressData,
  }
}
